import React, { useState, useCallback, useRef } from "react";
import {
    TextField,
    InputAdornment,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItemButton,
    ListItemText,
    Typography,
    CircularProgress,
    Box,
    } from "../../ui";
import { Search,
    SearchOff } from "../../ui/icons";
import { useDispatch,
    useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { snpAxios } from "../../global/api";
import {
  CART_INITIAL_STATE,
  GETBILLBYREGEX,
  getPreUrlAccordingToCart,
  SUCCESS,
  isEmpty
} from "../../../constants/constant";
import DismissButton from "../../global/DismissButton";

const BillSearchBar = () => {
  const [showBillSearchDialog, setShowBillSearchDialog] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  const storeActiveShopHash = useSelector(
    (state) => state.setActiveShopHashReducer.activeShopHash
  );
  const storeApiUrl = useSelector(
    (state) => state.setApiUrlReducer.MAINURL.INITIAL_BASE_URL
  );

  const fetchBills = useCallback(
    async (query) => {
      if (query.length < 3) {
        setSearchResults([]);
        setNoResults(false);
        return;
      }

      setIsSearching(true);
      setNoResults(false);
      try {
        const fetchBillUrl = `${storeApiUrl.BACKEND_BASE_URL}${GETBILLBYREGEX}?adminId=${CART_INITIAL_STATE.apiToken}&shopHash=${storeActiveShopHash.id}&regexStr=${query}`;
        const response = await snpAxios.post(fetchBillUrl);
        if (response.data.status === SUCCESS) {
          const bills = response.data.jcJson.listBills;
          setSearchResults(bills || []);
          setNoResults(isEmpty(bills));
        } else {
          setSearchResults([]);
          setNoResults(true);
        }
      } catch (error) {
        console.error("BillSearchBar fetchBills error:", error);
        setSearchResults([]);
        setNoResults(true);
      }
      setIsSearching(false);
    },
    [storeApiUrl, storeActiveShopHash]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchBills(value);
    }, 400);
  };

  const handleBillSelect = (bill) => {
    setShowBillSearchDialog(false);
    setSearchValue("");
    setSearchResults([]);
    navigate("/dashboard/billentry", {
      state: { tableProps: {}, sBillNo: bill.billNo, billType: bill.billType },
    });
  };

  const handleDialogClose = () => {
    setShowBillSearchDialog(false);
    setSearchValue("");
    setSearchResults([]);
    setNoResults(false);
  };

  return (
    <>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        placeholder="Search Bill..."
        onClick={() => setShowBillSearchDialog(true)}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          "& .UiOutlinedInput-root": {
            cursor: "pointer",
            fontSize: "12px",
          },
        }}
      />
      <Dialog
        open={showBillSearchDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "14px", textAlign: "center" }}>
          Search Bill by Number / Name
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Type at least 3 characters..."
            value={searchValue}
            onChange={handleSearchChange}
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isSearching ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SearchOff fontSize="small" />
                  )}
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
          {noResults && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 2 }}
            >
              No bills found for "{searchValue}"
            </Typography>
          )}
          {searchResults.length > 0 && (
            <List dense>
              {searchResults.map((bill) => (
                <ListItemButton
                  key={bill.billNo}
                  onClick={() => handleBillSelect(bill)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight="bold">
                        {bill.billNo}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {bill.billTypeName}
                        {bill.finalPrice ? ` - ₹${bill.finalPrice}` : ""}
                        {bill.custName ? ` | ${bill.custName}` : ""}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <DismissButton variant="contained" onClick={handleDialogClose}>
            Close
          </DismissButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BillSearchBar;